import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import 'iconify-icon';
import axios from 'axios';
import { toast } from 'sonner';
import { trackFormSubmission } from '../lib/analytics';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const JoinTeam = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', email: '', phone: '', city: '', area: '', message: '', resume_url: '' });
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeUploading, setResumeUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleResumeUpload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('join_team.resume_too_big'));
      return;
    }
    setResumeUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API_URL}/public/upload-resume`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResumeFile({ name: file.name, url: data.url });
      setForm((p) => ({ ...p, resume_url: data.url }));
      toast.success('Currículo enviado!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Falha ao enviar currículo');
    } finally {
      setResumeUploading(false);
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.animate-on-scroll').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const opportunities = [
    {
      title: 'Facilitador de Eneagrama',
      location: 'Várias Franquias',
      type: 'Presencial',
      description: 'Ministrar treinamentos e workshops de Eneagrama para indivíduos e empresas'
    },
    {
      title: 'Consultor Corporativo',
      location: 'Home Office / Híbrido',
      type: 'Híbrido',
      description: 'Atender empresas com consultoria em desenvolvimento organizacional'
    },
    {
      title: 'Coordenador de Franquia',
      location: 'Conforme Região',
      type: 'Presencial',
      description: 'Gerenciar operações e equipe de franquia regional'
    },
    {
      title: 'Designer Instrucional',
      location: 'Home Office',
      type: 'Remoto',
      description: 'Desenvolver materiais didáticos e conteúdos para programas'
    }
  ];

  const values = [
    {
      icon: 'solar:heart-linear',
      title: 'Propósito',
      description: 'Trabalhe com o que realmente importa: transformação de vidas'
    },
    {
      icon: 'solar:users-group-rounded-linear',
      title: 'Equipe Colaborativa',
      description: 'Ambiente de trabalho acolhedor e colaborativo'
    },
    {
      icon: 'solar:diploma-verified-linear',
      title: 'Desenvolvimento Contínuo',
      description: 'Investimos no crescimento profissional da nossa equipe'
    },
    {
      icon: 'solar:star-linear',
      title: 'Reconhecimento',
      description: 'Valorizamos e reconhecemos o talento e dedicação'
    }
  ];

  return (
    <div>
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-24 border-b border-neutral-900 relative overflow-hidden">
          <div className="absolute inset-0 bg-grid opacity-30"></div>
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <span className="text-xs font-mono uppercase tracking-[0.3em] text-gray-400 mb-4 block animate-on-scroll">
                {t('pages.join_team.hero_label')}
              </span>
              <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6 animate-on-scroll delay-100">
                {t('pages.join_team.title_part1')} <span className="text-accent-orange">{t('pages.join_team.title_highlight')}</span>
              </h1>
              <p className="text-lg text-neutral-400 mb-8 leading-relaxed animate-on-scroll delay-200">
                {t('pages.join_team.hero_subtitle')}
              </p>
              <div className="flex flex-wrap justify-center gap-4 animate-on-scroll delay-300">
                <a href="#vagas" className="beam-btn group">
                  <div className="beam-spinner"></div>
                  <div className="beam-btn-content">
                    <span>{t('pages.join_team.see_opportunities')}</span>
                    <iconify-icon icon="solar:arrow-right-linear"></iconify-icon>
                  </div>
                </a>
                <a href="#cultura" className="px-8 py-4 text-sm font-mono uppercase tracking-wider text-neutral-400 hover:text-[#EAEAEA] transition-colors">
                  {t('pages.join_team.our_culture')}
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Culture & Values */}
        <section id="cultura" className="py-24 relative" style={{ background: "rgba(10, 10, 10, 0.72)" }}>
          <div className="max-w-[1440px] mx-auto px-6 md:px-12">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold mb-4 animate-on-scroll">{t('pages.join_team.culture_title')}</h2>
              <p className="text-lg text-neutral-400 animate-on-scroll delay-100">{t('pages.join_team.culture_subtitle')}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, index) => (
                <div
                  key={index}
                  className="glow-card bg-[#111111] p-8 rounded-xl text-center hover-lift animate-on-scroll"
                  style={{ transitionDelay: `${index * 0.1}s` }}
                >
                  <div className="w-16 h-16 rounded-full bg-[#1F1F1F] border border-[#333333] flex items-center justify-center text-accent-orange mx-auto mb-4">
                    <iconify-icon icon={value.icon} className="text-4xl"></iconify-icon>
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{value.title}</h3>
                  <p className="text-neutral-400">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Open Positions */}
        <section id="vagas" className="py-24 bg-[#111111] border-t border-neutral-800">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold mb-4 animate-on-scroll">{t('pages.join_team.openings_title')}</h2>
              <p className="text-lg text-neutral-400 animate-on-scroll delay-100">{t('pages.join_team.openings_subtitle')}</p>
            </div>
            <div className="max-w-4xl mx-auto space-y-6">
              {opportunities.map((job, index) => (
                <div
                  key={index}
                  className="glow-card bg-[#1A1A1A] border border-[#333333] p-8 rounded-xl hover-lift animate-on-scroll"
                  style={{ transitionDelay: `${index * 0.1}s` }}
                >
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
                    <div className="flex-1">
                      <h3 className="text-2xl font-semibold mb-2">{job.title}</h3>
                      <div className="flex flex-wrap gap-4 text-sm text-neutral-400">
                        <span className="flex items-center gap-1">
                          <iconify-icon icon="solar:map-point-linear"></iconify-icon>
                          {job.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <iconify-icon icon="solar:case-linear"></iconify-icon>
                          {job.type}
                        </span>
                      </div>
                    </div>
                    <Button className="bg-black text-white hover:bg-neutral-800">
                      {t('pages.join_team.apply')}
                    </Button>
                  </div>
                  <p className="text-neutral-400">{job.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Application Form */}
        <section className="relative py-24 text-white" style={{ background: 'rgba(10, 10, 10, 0.78)' }}>
          <div className="max-w-[1440px] mx-auto px-6 md:px-12">
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-5xl font-bold mb-4 text-white">{t('pages.join_team.form_title')}</h2>
                <p className="text-lg text-white/80">{t('pages.join_team.form_subtitle')}</p>
              </div>
              <form className="space-y-4" onSubmit={async (e) => {
                e.preventDefault();
                setSubmitting(true);
                try {
                  await axios.post(`${API_URL}/public/job-application`, form);
                  trackFormSubmission('trabalhe_conosco');
                  toast.success(t('join_team.success'));
                  setForm({ name: '', email: '', phone: '', city: '', area: '', message: '' });
                } catch (err) {
                  toast.error(err.response?.data?.detail || t('contact.error'));
                } finally {
                  setSubmitting(false);
                }
              }} data-testid="job-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder={t('contact.field_name')}
                    data-testid="job-name-input"
                    className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-4 py-3 text-sm text-white placeholder-white/50 outline-none focus:border-white/40"
                  />
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder={t('contact.field_email')}
                    data-testid="job-email-input"
                    className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-4 py-3 text-sm text-white placeholder-white/50 outline-none focus:border-white/40"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder={t('contact.field_phone')}
                    data-testid="job-phone-input"
                    className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-4 py-3 text-sm text-white placeholder-white/50 outline-none focus:border-white/40"
                  />
                  <input
                    type="text"
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    placeholder={t('franchisee.field_city')}
                    data-testid="job-city-input"
                    className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-4 py-3 text-sm text-white placeholder-white/50 outline-none focus:border-white/40"
                  />
                </div>
                <select
                  value={form.area}
                  onChange={(e) => setForm({ ...form, area: e.target.value })}
                  data-testid="job-area-select"
                  className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-4 py-3 text-sm text-white outline-none focus:border-white/40"
                >
                  <option value="" className="bg-neutral-800">{t('join_team.field_area')}</option>
                  <option className="bg-neutral-800">Facilitador de Eneagrama</option>
                  <option className="bg-neutral-800">Consultor Corporativo</option>
                  <option className="bg-neutral-800">Coordenador de Franquia</option>
                  <option className="bg-neutral-800">Designer Instrucional</option>
                  <option className="bg-neutral-800">{t('contact.subject_options.other')}</option>
                </select>
                <div
                  className="border-2 border-dashed border-white/20 rounded-lg p-6 text-center cursor-pointer hover:border-accent-orange transition-colors"
                  data-testid="job-resume-dropzone"
                >
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => handleResumeUpload(e.target.files?.[0])}
                    className="hidden"
                    id="resume-upload"
                    data-testid="job-resume-input"
                    disabled={resumeUploading}
                  />
                  <label htmlFor="resume-upload" className="block cursor-pointer">
                    {resumeUploading ? (
                      <div className="flex flex-col items-center gap-2">
                        <div className="animate-spin h-6 w-6 border-2 border-white/30 border-t-white rounded-full"></div>
                        <p className="text-white/80 text-sm">{t('join_team.resume_uploading')}</p>
                      </div>
                    ) : resumeFile ? (
                      <div className="flex flex-col items-center gap-2">
                        <iconify-icon icon="solar:check-circle-bold" className="text-4xl text-accent-orange"></iconify-icon>
                        <p className="text-white text-sm font-medium">{resumeFile.name}</p>
                        <p className="text-white/50 text-xs">{t('join_team.resume_attached')}</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <iconify-icon icon="solar:upload-linear" className="text-4xl text-white/50"></iconify-icon>
                        <p className="text-white/80 text-sm">{t('join_team.resume_upload')}</p>
                        <p className="text-white/40 text-xs">{t('join_team.resume_max')}</p>
                      </div>
                    )}
                  </label>
                </div>
                <textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder={t('join_team.field_message')}
                  rows="4"
                  data-testid="job-message-input"
                  className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-4 py-3 text-sm text-white placeholder-white/50 outline-none focus:border-white/40 resize-none"
                ></textarea>
                <Button type="submit" disabled={submitting} data-testid="job-submit-btn" className="w-full bg-accent-orange text-white hover:bg-[#E55D00] h-12 text-sm font-bold uppercase tracking-wider">
                  {submitting ? t('common.sending') : t('join_team.submit')}
                </Button>
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default JoinTeam;
